import React from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import {
  HiOutlineChartBar,
  HiOutlineUsers,
  HiOutlineMegaphone,
  HiOutlineXMark,
  HiOutlineArrowLeft,
  HiOutlineShieldCheck,
  HiOutlineLockClosed,
  HiOutlineCreditCard,
  HiOutlineCog6Tooth,
  HiOutlineQueueList,
  HiOutlineBeaker,
  HiOutlineGlobeAlt,
  HiOutlineKey,
  HiOutlineDevicePhoneMobile,
} from 'react-icons/hi2'

const SuperAdminSidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleBackToDashboard = () => {
    navigate('/dashboard')
  }

  const allNavItems = [
    {
      name: 'Dashboard',
      path: '/superadmin',
      icon: HiOutlineChartBar,
      end: true,
      roles: ['superadmin'] // Only superadmin can see
    },
    {
      name: 'Subscribers',
      path: '/superadmin/subscribers',
      icon: HiOutlineUsers,
      roles: ['superadmin'] // Only superadmin can see
    },
    {
      name: 'Permissions',
      path: '/superadmin/permissions',
      icon: HiOutlineLockClosed,
      roles: ['superadmin'] // Only superadmin can see
    },
    {
      name: 'Communications',
      path: '/superadmin/communications',
      icon: HiOutlineMegaphone,
      roles: ['superadmin', 'assistant'] // Both can see
    },
    {
      name: 'Landing Page',
      path: '/superadmin/landing-cms',
      icon: HiOutlineGlobeAlt,
      roles: ['superadmin']
    },
    {
      name: 'Auth Screens',
      path: '/superadmin/auth-screen',
      icon: HiOutlineKey,
      roles: ['superadmin']
    },
    {
      name: 'App Releases',
      path: '/superadmin/app-releases',
      icon: HiOutlineDevicePhoneMobile,
      roles: ['superadmin']
    },
    // Phase 3 — Drive-First Pivot
    {
      name: 'Subscriptions',
      path: '/superadmin/subscriptions',
      icon: HiOutlineCreditCard,
      roles: ['superadmin']
    },
    {
      name: 'Subscription Settings',
      path: '/superadmin/subscription-settings',
      icon: HiOutlineCog6Tooth,
      roles: ['superadmin']
    },
    {
      name: 'Stripe Events',
      path: '/superadmin/stripe-events',
      icon: HiOutlineQueueList,
      roles: ['superadmin']
    },
    {
      name: 'Mock Payments',
      path: '/superadmin/mock-payments',
      icon: HiOutlineBeaker,
      roles: ['superadmin']
    },
  ]

  // Filter nav items based on user role
  const navItems = allNavItems.filter(item => 
    item.roles.includes(user?.role)
  )

  // Determine if user is assistant
  const isAssistant = user?.role === 'assistant'

  return (
    <>
      {/* Sidebar Container */}
      <aside 
        className={`
          fixed lg:static left-0 z-50
          transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0
          transition duration-300 ease-in-out
          w-64 bg-gradient-to-b from-red-600 to-red-700 text-white
          flex flex-col
        `}
        style={{ top: '30px', bottom: 0 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-red-500">
          <div className="flex items-center space-x-2">
            <HiOutlineShieldCheck className="w-8 h-8 text-red-100" />
            <div>
              <h1 className="text-lg font-bold">Superadmin</h1>
              <p className="text-xs text-red-200">System Management</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden text-white hover:text-red-200"
          >
            <HiOutlineXMark className="w-6 h-6" />
          </button>
        </div>

        {/* User Info */}
        <div className="p-4 bg-red-700 bg-opacity-50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-red-800 flex items-center justify-center">
              <span className="text-lg font-semibold">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-red-200 truncate">{user?.email}</p>
              <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-red-800 rounded-full">
                {isAssistant ? 'Assistant' : 'Superadmin'}
              </span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-2">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-red-800 text-white'
                    : 'text-red-100 hover:bg-red-600'
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.name}</span>
            </NavLink>
          ))}
        </nav>

        {/* Footer Actions */}
        <div className="p-4 border-t border-red-500 space-y-2">
          {/* Only show Back to Dashboard for superadmin */}
          {!isAssistant && (
            <button
              onClick={handleBackToDashboard}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-800 hover:bg-red-900 rounded-lg transition-colors"
            >
              <HiOutlineArrowLeft className="w-5 h-5" />
              <span>Back to Dashboard</span>
            </button>
          )}
          <button
            onClick={handleLogout}
            className="w-full px-4 py-2 bg-red-500 bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-colors text-sm"
          >
            Logout
          </button>
        </div>
      </aside>
    </>
  )
}

export default SuperAdminSidebar

